import React from "react";
import { Box, Typography, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export default function ComplaintsPolicyPage() {
  const classes = useStyles();
  const seo = useSEO("complaints-policy");

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h3" className={classes.title}>
          Complaints and Takedown Policy
        </Typography>

        <Typography variant="body2" className={classes.lastUpdated}>
          Effective Date: June 30, 2025
        </Typography>

        <Typography variant="body2" className={classes.lastUpdated}>
          Last Updated: June 30, 2025
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          At Sugar Cribs, we are committed to maintaining a respectful and
          legally compliant environment for all users, creators, and
          individuals depicted in content on our platform. If you encounter
          content that you believe is illegal, violates our Terms of
          Service, or infringes upon your rights, you may submit a complaint
          or takedown request using the procedures outlined below.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          1. Reporting Inappropriate or Illegal Content
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We encourage users and third parties to report:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Content that may be illegal or violates community standards
          </li>
          <li className={classes.listItem}>Content uploaded without valid consent</li>
          <li className={classes.listItem}>
            Any suspected violations of 18 U.S.C. § 2257 or CCBill's
            Acceptable Use Policy
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>To submit a report,</strong> please contact us at:
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          📧 <strong>Email:</strong>{" "}
          <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Please include:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>The URL of the content in question</li>
          <li className={classes.listItem}>A brief description of the issue</li>
          <li className={classes.listItem}>Your contact information</li>
          <li className={classes.listItem}>Any relevant supporting documentation</li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          You may also submit a formal complaint through our payment
          processor here:
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <a href="https://www.ccbillcomplaintform.com/ccbill/form/CCBillContentRemovalRequest1/formperma/sBK2jfIoZWAFw2hRRt5Rv2PQncscFzpvOH6bPcwopas" className={classes.link}>
            🔗 Submit a Complaint via CCBill
          </a>
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          2. Review & Resolution Process
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          All reported complaints will be reviewed by our compliance team
          within <strong>five (5) business days.</strong> During this time,
          we may:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            Contact the content uploader for clarification or evidence of
            consent
          </li>
          <li className={classes.listItem}>Temporarily disable access to the content in question</li>
          <li className={classes.listItem}>
            Review associated age verification and consent documentation
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          If the content is found to violate our policies or applicable law,
          it will be promptly removed.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          3. Appeals Process
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If a report is denied and you disagree with the outcome, you may
          submit an appeal by contacting us again at{" "}
          <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>{" "}
          with the subject line:{" "}
          <strong>"Appeal – Takedown Request."</strong>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          We will conduct a secondary review. If the matter remains
          unresolved or disputed, Sugar Cribs will allow the issue to be
          reviewed by a <strong>neutral third-party mediator</strong> to
          ensure fairness.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          4. Outcomes of Review
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Depending on the nature of the complaint and the evidence
          provided, the following outcomes may apply:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Immediate removal of content</li>
          <li className={classes.listItem}>Temporary suspension of the uploader's account</li>
          <li className={classes.listItem}>Permanent account termination</li>
          <li className={classes.listItem}>No action taken (with documented justification)</li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          We will notify the complainant and the uploader (if applicable) of
          the final decision in writing.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          5. Depicted Person Requesting Removal
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you are <strong>depicted in any content</strong> on Sugar Cribs
          and would like it removed, even if you originally consented, you
          may contact us using the process above. Sugar Cribs honors all
          valid takedown requests involving lack of consent or legal status
          change of consent (e.g. revocation under local laws).
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          Need Help?
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you have any questions about this policy or need help
          submitting a report, please contact:
        </Typography>

        <Box className={classes.contactInfo}>
          <Typography
            variant="h6"
            style={{ color: "#FE5A97", marginBottom: "16px" }}
          >
            Contact Information
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            📧{" "}
            <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          </Typography>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          We take all complaints seriously and aim to resolve issues
          promptly while maintaining a safe space for all users.
        </Typography>
      </Container>
    </>
  );
}
